import i18n, { getBaseURL } from "./i18n";
import { entityToLocaleFolder } from "./entityLocaleFolder";

export const COUNTRY_BLOCKED = "COUNTRY_BLOCKED";

// Message-type "buckets" (CX-51004 / CX-51021). A bucket key must match the
// config array name in restrictedCountries.json exactly (no name-transform)
// and the i18n key shipped in every locale's language.json. The prefix is the
// only thing this file hardcodes — the lookup below discovers COUNTRY_BLOCKED_
// MESSAGE_N keys directly from whatever the config object actually contains,
// so a new tier (message-4, -5, ...) needs zero code changes here, only a new
// locale key + a new restrictedCountries.json array (per CX-51021: "switching
// of messages relatively quickly between releases" without FE redeploys).
export const COUNTRY_BLOCKED_MESSAGE_PREFIX = "COUNTRY_BLOCKED_MESSAGE_";

export const countryBlockedMessageKeyFor = (tier) => `${COUNTRY_BLOCKED_MESSAGE_PREFIX}${tier}`;

const BUCKET_KEY_PATTERN = new RegExp(`^${COUNTRY_BLOCKED_MESSAGE_PREFIX}(\\d+)$`);

// Discovers every COUNTRY_BLOCKED_MESSAGE_N key present on the config object,
// ordered ascending by N — so message-1 keeps first-match priority over
// message-2, which keeps it over message-3, and so on for any future tier.
const discoverBucketKeys = (config) => {
	if (!config || typeof config !== "object") return [];
	return Object.keys(config)
		.map((key) => {
			const match = key.match(BUCKET_KEY_PATTERN);
			return match ? { key, n: Number(match[1]) } : null;
		})
		.filter(Boolean)
		.sort((a, b) => a.n - b.n)
		.map((entry) => entry.key);
};

export const COUNTRY_BLOCKED_FALLBACK_TEXT =
	"Onboarding and login are not currently available for your country of residence.";

const usableValue = (value, key) =>
	typeof value === "string" && value.trim() !== "" && value !== key ? value : null;

export const countryBlockedKeyFor = (countryCode) =>
	countryCode ? `${COUNTRY_BLOCKED}_${String(countryCode).toUpperCase()}` : null;

// Builds the country -> bucket-key lookup once per distinct config object
// (cached by reference) rather than re-scanning the bucket arrays on every
// getCountryBlockedMessage call.
let cachedConfig = null;
let cachedLookup = null;

const buildCountryBucketLookup = (config) => {
	const lookup = {};
	const countryBlockedSet = new Set((config?.countryBlocked || []).map((code) => String(code).toUpperCase()));

	discoverBucketKeys(config).forEach((bucketKey) => {
		const countries = Array.isArray(config?.[bucketKey]) ? config[bucketKey] : [];
		countries.forEach((rawCode) => {
			const code = String(rawCode).toUpperCase();

			// A bucket entry that isn't in countryBlocked is a config error.
			// Blocking membership is decided solely by isCountryBlockedResponse
			// + the BE response — bucket membership must never grant it, so the
			// entry is dropped entirely (never added to the lookup).
			if (!countryBlockedSet.has(code)) {
				// eslint-disable-next-line no-console
				console.warn(
					`countryBlocked: "${code}" is listed in ${bucketKey} but is not present in countryBlocked — ignoring for message-bucket routing.`
				);
				return;
			}

			if (lookup[code]) {
				// eslint-disable-next-line no-console
				console.warn(
					`countryBlocked: "${code}" appears in more than one COUNTRY_BLOCKED_MESSAGE bucket (${lookup[code]} and ${bucketKey}) — using ${lookup[code]}.`
				);
				return;
			}

			lookup[code] = bucketKey;
		});
	});

	// CX-51021 (post-launch scope correction): the generic COUNTRY_BLOCKED key
	// is retired from the display candidate chain below — every blocked country
	// is now expected to be assigned to exactly one COUNTRY_BLOCKED_MESSAGE_N
	// tier. A countryBlocked entry that lands in no bucket is therefore a real
	// config gap (not a benign fallback case) and falls through all the way to
	// the hardcoded COUNTRY_BLOCKED_FALLBACK_TEXT for that country — warn loudly
	// so the gap gets caught at config-review time rather than silently shipping
	// generic copy in production.
	countryBlockedSet.forEach((code) => {
		if (!lookup[code]) {
			// eslint-disable-next-line no-console
			console.warn(
				`countryBlocked: "${code}" is present in countryBlocked but is not assigned to any COUNTRY_BLOCKED_MESSAGE_N bucket — it will render the hardcoded fallback text instead of a message tier. Assign it to a bucket in restrictedCountries.json.`
			);
		}
	});

	return lookup;
};

const getCountryBucketLookup = (config) => {
	if (!config) return null;
	if (config === cachedConfig && cachedLookup) return cachedLookup;
	cachedConfig = config;
	cachedLookup = buildCountryBucketLookup(config);
	return cachedLookup;
};

/**
 * Resolves the COUNTRY_BLOCKED_MESSAGE_N bucket key a country belongs to,
 * given the restrictedCountries.json config. Returns null when no config is
 * supplied or the country isn't in any (valid) bucket.
 */
export const countryBlockedBucketKeyFor = (countryCode, restrictedCountriesConfig) => {
	const lookup = getCountryBucketLookup(restrictedCountriesConfig);
	if (!lookup || !countryCode) return null;
	return lookup[String(countryCode).toUpperCase()] || null;
};

/**
 * The helper checks all three formats and tells the screen whether the response means the user is blocked by country.
 *   - orchestration_ms ApiErrorResponse (sign-up 400, auto-login / forgot
 *     username / forgot password 401)      -> payload.message
 *   - the same, wrapped by a service catch -> payload.errData.message
 *   - auto-login success envelope          -> payload.data["login-response"].message
 */
export const isCountryBlockedResponse = (payload) =>
	Boolean(
		payload &&
			(payload.message === COUNTRY_BLOCKED ||
				payload.errData?.message === COUNTRY_BLOCKED ||
				payload.data?.["login-response"]?.message === COUNTRY_BLOCKED)
	);

/**
 * Resolve the blocking message for display. All display copy comes from translation
 * keys, in priority order:
 *   1. per-country override — COUNTRY_BLOCKED_<CODE> (kept as a hotfix lever)
 *   2. the country's COUNTRY_BLOCKED_MESSAGE_N bucket (config-driven, optional
 *      3rd param — omitted/no match falls through unchanged)
 *   3. the en-US resource bundle, checked in the same order (1, then 2)
 *   `COUNTRY_BLOCKED_FALLBACK_TEXT` — last-resort safety net only. Every
 *   blocked country is expected to be in exactly one bucket (CX-51021); a
 *   country that reaches this point is a config gap (buildCountryBucketLookup
 *   already warned about it), not a normal path. The legacy generic
 *   COUNTRY_BLOCKED key is deliberately NOT part of this chain — CX-51021
 *   retires the single shared message in favor of the tiered system.
 */
// Walks `candidates` in priority order, returning the first value `lookupFn`
// resolves that survives `usableValue` (non-blank, not an echoed key). Shared
// by the sync (live i18next instance) and async (fetched cross-entity bundle)
// resolution paths below so both apply the exact same candidate-chain rules.
const resolveCandidates = (candidates, lookupFn) => {
	for (const key of candidates) {
		const value = usableValue(lookupFn(key), key);
		if (value) return value;
	}
	return null;
};

export const getCountryBlockedMessage = (getLabel, countryCode, restrictedCountriesConfig) => {
	const bucketKey = countryBlockedBucketKeyFor(countryCode, restrictedCountriesConfig);
	const candidates = [countryBlockedKeyFor(countryCode), bucketKey].filter(Boolean);

	const activeValue = resolveCandidates(candidates, (key) =>
		i18n.exists(key) ? (typeof getLabel === "function" ? getLabel(key) : i18n.t(key)) : undefined
	);
	if (activeValue) return activeValue;

	const enBundle = i18n.getResourceBundle?.("en-US", "language");
	const enValue = resolveCandidates(candidates, (key) => enBundle?.[key]);
	if (enValue) return enValue;

	return COUNTRY_BLOCKED_FALLBACK_TEXT;
};

// --- CX-51004/CX-51021 gap fix -------------------------------------------
// getCountryBlockedMessage above resolves against whichever entity i18next
// currently has loaded (driven by SELECTED_ENTITY in localStorage) — but
// that isn't necessarily the entity that owns the blocked country (e.g. a
// user blocked with countryOfResidence "ZM", owned by FXCM_SVG, while the
// app's active entity/i18n bundle is something else — direct-login,
// forgot-username, forgot-password, or a stale/racing entity switch never
// guarantee a match). The functions below resolve the OWNING entity for a
// country and fetch that entity's bundle directly, independent of whatever
// i18next currently has loaded, so the copy is always correct regardless of
// the app's currently active entity.

// Cached after a successful fetch (Map<COUNTRY_CODE, entity_code>); a failed
// fetch is deliberately NOT cached so the next call retries rather than
// permanently degrading to the fallback path for the rest of the session.
let countryEntityMapPromise = null;

const buildCountryEntityMap = async () => {
	const response = await fetch(`${getBaseURL()}/configurations/countryList.json`);
	const data = await response.json();
	const map = {};
	(Array.isArray(data) ? data : []).forEach((entry) => {
		if (entry?.country_code && entry?.entity_code) {
			map[String(entry.country_code).toUpperCase()] = entry.entity_code;
		}
	});
	return map;
};

/**
 * Resolves the `entity_code` (per countryList.json) that owns `countryCode`.
 * Returns null — never throws — when the country isn't in countryList.json
 * or the fetch/parse fails, so callers can gracefully degrade.
 */
export const resolveCountryEntityCode = async (countryCode) => {
	if (!countryCode) return null;
	if (!countryEntityMapPromise) {
		countryEntityMapPromise = buildCountryEntityMap().catch((e) => {
			// eslint-disable-next-line no-console
			console.warn(`countryBlocked: failed to load countryList.json for country->entity resolution — ${e?.message || e}`);
			countryEntityMapPromise = null; // don't cache the failure — allow a retry next call
			return null;
		});
	}
	const map = await countryEntityMapPromise;
	if (!map) return null;
	return map[String(countryCode).toUpperCase()] || null;
};

// Cached by "{folder}:{lang}" (Map) so repeated blocked-country renders in
// the same session don't refetch. A failed fetch is not cached, mirroring
// resolveCountryEntityCode's retry behavior.
const entityBundleCache = new Map();

/**
 * Fetches a specific entity's language.json bundle directly — independent
 * of i18next's own currently-loaded resources, since i18next's backend
 * loadPath is closed over the app's CURRENT SELECTED_ENTITY and can't be
 * asked to load a different entity's bundle for a one-off lookup without
 * side-effecting the live app language state. Returns null — never throws —
 * on any fetch/parse failure.
 */
export const fetchEntityLanguageBundle = async (entityFolder, lang) => {
	if (!entityFolder || !lang) return null;
	const cacheKey = `${entityFolder}:${lang}`;
	if (entityBundleCache.has(cacheKey)) return entityBundleCache.get(cacheKey);

	try {
		const response = await fetch(`${getBaseURL()}/locales/${entityFolder}/${lang}/language.json`);
		const bundle = await response.json();
		entityBundleCache.set(cacheKey, bundle);
		return bundle;
	} catch (e) {
		// eslint-disable-next-line no-console
		console.warn(`countryBlocked: failed to fetch cross-entity locale bundle for ${entityFolder}/${lang} — ${e?.message || e}`);
		return null;
	}
};

// Test-only: clears the module-level caches above so tests can exercise
// fresh fetch/cache-miss behavior independently of test execution order.
export const __resetCountryBlockedCrossEntityCaches = () => {
	countryEntityMapPromise = null;
	entityBundleCache.clear();
};

/**
 * Async counterpart to getCountryBlockedMessage (CX-51004/CX-51021 gap fix).
 * Resolves the blocking message from the blocked country's OWNING entity's
 * bundle — not from whatever entity the app's i18next instance currently has
 * loaded — using the exact same candidate-chain priority (per-country
 * override, then bucket key), just sourced from the fetched bundle instead
 * of i18n.exists/i18n.t.
 *
 * Falls back to the existing synchronous getCountryBlockedMessage (i.e.
 * today's behavior, resolved against the live i18next instance) whenever:
 *   - the country isn't in countryList.json,
 *   - the countryList.json fetch fails,
 *   - the entity's locale bundle fetch fails, or
 *   - the fetched bundle(s) have no usable value for any candidate key.
 * Never throws.
 */
export const resolveCountryBlockedMessage = async (getLabel, countryCode, restrictedCountriesConfig, lang) => {
	// Outermost guard: even a bug in the candidate-chain logic below (e.g. a
	// misbehaving getLabel, or i18n.exists throwing) must never turn into a
	// rejected promise — callers `.then()` this without a `.catch()`, by
	// design (see the 5 call sites), so an unhandled rejection here would
	// otherwise escape all the way up to the page/component.
	try {
		const bucketKey = countryBlockedBucketKeyFor(countryCode, restrictedCountriesConfig);
		const candidates = [countryBlockedKeyFor(countryCode), bucketKey].filter(Boolean);
		const activeLang = lang || i18n.language || "en-US";

		if (candidates.length) {
			try {
				const entityCode = await resolveCountryEntityCode(countryCode);
				const entityFolder = entityToLocaleFolder(entityCode);

				if (entityFolder) {
					const bundle = await fetchEntityLanguageBundle(entityFolder, activeLang);
					let value = bundle && resolveCandidates(candidates, (key) => bundle[key]);

					if (!value && activeLang !== "en-US") {
						const enBundle = await fetchEntityLanguageBundle(entityFolder, "en-US");
						value = enBundle && resolveCandidates(candidates, (key) => enBundle[key]);
					}

					if (value) return value;
				}
			} catch (e) {
				// eslint-disable-next-line no-console
				console.warn(`countryBlocked: cross-entity resolution failed for country "${countryCode}" — ${e?.message || e}`);
			}
		}

		return getCountryBlockedMessage(getLabel, countryCode, restrictedCountriesConfig);
	} catch (e) {
		// eslint-disable-next-line no-console
		console.warn(`countryBlocked: resolveCountryBlockedMessage failed unexpectedly for country "${countryCode}" — ${e?.message || e}`);
		return COUNTRY_BLOCKED_FALLBACK_TEXT;
	}
};
